import {
  PLAN_AUDIENCE_IDS,
  planKeyFromId,
  type TFlag,
  type TFlagConfig,
  type TPlanKey,
} from "@/lib/feature-flags";

export type TEditableFlag = {
  id: string;
  name: string;
  enabled: boolean;
  allowlistOrgIds: string[];
  blocklistOrgIds: string[];
  rolloutPercentage: number;
  audiences: TPlanKey[];
  description?: string;
  createdAt?: string;
  createdById?: string;
};

export const configToEditable = (config: TFlagConfig): TEditableFlag[] =>
  Object.keys(config.flags).map((key) => {
    const f = config.flags[key];
    return {
      id: key,
      name: f.name,
      enabled: !!f.enabled,
      allowlistOrgIds: f.allowlistOrgIds ?? [],
      blocklistOrgIds: f.blocklistOrgIds ?? [],
      rolloutPercentage: f.rolloutPercentage ?? 0,
      audiences: (f.audiences ?? [])
        .map(planKeyFromId)
        .filter((key): key is TPlanKey => key !== null),
      description: f.description,
      createdAt: f.createdAt,
      createdById: f.createdById,
    };
  });

export const editableToConfig = (
  flags: TEditableFlag[],
  version: number,
): TFlagConfig => {
  const out: Record<string, TFlag> = {};
  for (const ef of flags) {
    const name = ef.name.trim();
    out[name] = {
      name,
      enabled: ef.enabled,
      allowlistOrgIds: ef.allowlistOrgIds,
      blocklistOrgIds: ef.blocklistOrgIds,
      rolloutPercentage: ef.rolloutPercentage,
      ...(ef.audiences.length > 0
        ? { audiences: ef.audiences.map((key) => PLAN_AUDIENCE_IDS[key]) }
        : {}),
      ...(ef.description ? { description: ef.description } : {}),
      ...(ef.createdAt ? { createdAt: ef.createdAt } : {}),
      ...(ef.createdById ? { createdById: ef.createdById } : {}),
    };
  }
  return { version, flags: out };
};

export const blankFlag = (id: string): TEditableFlag => ({
  id,
  name: "",
  enabled: false,
  allowlistOrgIds: [],
  blocklistOrgIds: [],
  rolloutPercentage: 0,
  audiences: [],
});

export const addFlag = (
  flags: TEditableFlag[],
  id: string,
): TEditableFlag[] => [...flags, blankFlag(id)];

export const removeFlag = (
  flags: TEditableFlag[],
  id: string,
): TEditableFlag[] => flags.filter((f) => f.id !== id);

export const updateFlag = (
  flags: TEditableFlag[],
  id: string,
  patch: Partial<TEditableFlag>,
): TEditableFlag[] => flags.map((f) => (f.id === id ? { ...f, ...patch } : f));

export const addOrgId = (list: string[], value: string): string[] => {
  const v = value.trim();
  if (!v || list.includes(v)) return list;
  return [...list, v];
};

export const removeOrgId = (list: string[], index: number): string[] =>
  list.filter((_, i) => i !== index);

export const togglePlan = (list: TPlanKey[], plan: TPlanKey): TPlanKey[] =>
  list.includes(plan) ? list.filter((p) => p !== plan) : [...list, plan];

export const validateEditable = (
  flags: TEditableFlag[],
): { ok: true } | { ok: false; error: string } => {
  const names = new Set<string>();
  for (const f of flags) {
    const name = f.name.trim();
    if (!name) return { ok: false, error: "Every flag needs a name." };
    if (names.has(name))
      return { ok: false, error: `Duplicate flag name: "${name}".` };
    names.add(name);
  }
  return { ok: true };
};
